import { Util } from '@antv/g6';
import { DrawOpts, NodeModel } from '../../../../types';
import { shapeNames, groupIds, ButtonType } from '../../../../shapes/constants';
import { getAbbreviatedNumber, getTextWidth, getChild } from '../../../../shapes/utils';

const DEFAULT_ICON_WIDTH = 16;
const DEFAULT_LINE_HEIGHT = 16;

const getNumUserText = (model: NodeModel) => getAbbreviatedNumber(model?.numUsers || 0);

const getNumUserOptions = (options: DrawOpts['options']) => ({
  numUsersIconStyle: options[shapeNames.numUsersIcon] || {},
  numUsersWrapperStyle: options[shapeNames.numUsersWrapper] || {},
  numUsersTextStyle: options[shapeNames.numUsersText] || {},
});

export const shouldNumUsersGroupShow = (numUsers: string | number) => {
  try {
    const num = typeof numUsers === 'string' ? parseInt(numUsers, 10) : numUsers;
    return num > 0;
  } catch (err) {
    return false;
  }
};

const getNumUserPositionDetails = ({ size, options }: Pick<DrawOpts, 'size' | 'options'>) => {
  const [width] = size || [0];
  const {
    numUsersWrapperStyle = {},
    numUsersTextStyle = {},
    numUsersIconStyle = {},
  } = getNumUserOptions(options);
  const { y = 0, lineWidth: borderWidth = 1 } = numUsersWrapperStyle;
  const { lineHeight } = numUsersTextStyle;
  const { iconWidth = DEFAULT_ICON_WIDTH } = numUsersIconStyle;

  const [wrapperPaddingTop, wrapperPaddingRight] = Util.formatPadding(numUsersWrapperStyle.padding);
  const [iconPaddingTop, , , iconPaddingLeft] = Util.formatPadding(numUsersIconStyle.padding);
  const [textPaddingTop, , , ,] = Util.formatPadding(numUsersTextStyle.padding);

  return {
    iconX: width - iconWidth + iconPaddingLeft,
    iconY: y + wrapperPaddingTop + iconPaddingTop,
    textX: width,
    textY: y + wrapperPaddingTop + textPaddingTop + lineHeight - borderWidth,
    wrapperX: width - iconWidth - wrapperPaddingRight,
  };
};

const getNumUserDetails = ({ size, options, model }: Omit<DrawOpts, 'group'>) => {
  const { iconX, iconY, textX, textY, wrapperX } = getNumUserPositionDetails({ size, options });
  const {
    numUsersWrapperStyle = {},
    numUsersTextStyle = {},
    numUsersIconStyle = {},
  } = getNumUserOptions(options);

  const { lineWidth: borderWidth = 1 } = numUsersWrapperStyle;
  const { lineHeight = DEFAULT_LINE_HEIGHT } = numUsersTextStyle;
  const { iconWidth = DEFAULT_ICON_WIDTH } = numUsersIconStyle;

  const [wrapperPaddingTop, wrapperPaddingRight, wrapperPaddingBottom, wrapperPaddingLeft] =
    Util.formatPadding(numUsersWrapperStyle.padding);
  const text = getNumUserText(model as NodeModel);
  const textWidth = getTextWidth(text, numUsersTextStyle);
  const wrapperWidth = wrapperPaddingRight + wrapperPaddingLeft + iconWidth + textWidth;
  const wrapperHeight = wrapperPaddingTop + wrapperPaddingBottom + borderWidth * 2 + lineHeight;

  return {
    iconX,
    iconY,
    text,
    textWidth,
    textX,
    textY,
    wrapperHeight,
    wrapperRadius: wrapperHeight / 2,
    wrapperWidth,
    wrapperX,
  };
};

export const drawNumUsers = ({ size, options, group, model }: DrawOpts) => {
  const { numUsersWrapperStyle, numUsersTextStyle, numUsersIconStyle } = getNumUserOptions(options);
  const { iconX, iconY, text, textX, textY, wrapperHeight, wrapperRadius, wrapperWidth, wrapperX } =
    getNumUserDetails({ size, options, model });
  const commonProps = { buttonType: ButtonType.NumUsers };

  const numUsersGroup = group.addGroup({
    ...commonProps,
    id: groupIds.numUsers,
    visible: shouldNumUsersGroupShow(model?.numUsers),
    name: shapeNames.numUsersGroup,
    className: shapeNames.numUsersGroup,
    draggable: true,
    capture: true,
  });

  const wrapper = numUsersGroup.addShape('rect', {
    ...commonProps,
    name: shapeNames.numUsersWrapper,
    className: shapeNames.numUsersWrapper,
    draggable: true,
    attrs: {
      x: wrapperX,
      width: wrapperWidth,
      height: wrapperHeight,
      radius: wrapperRadius,
      ...numUsersWrapperStyle,
    },
  });

  numUsersGroup.addShape('text', {
    ...commonProps,
    name: shapeNames.numUsersText,
    className: shapeNames.numUsersText,
    draggable: true,
    attrs: {
      text,
      x: textX,
      y: textY,
      ...numUsersTextStyle,
    },
  });

  numUsersGroup.addShape('image', {
    ...commonProps,
    name: shapeNames.numUsersIcon,
    className: shapeNames.numUsersIcon,
    draggable: true,
    attrs: {
      x: iconX,
      y: iconY,
      ...numUsersIconStyle,
    },
  });

  wrapper.toBack();

  return numUsersGroup;
};

export const updateNumUsers = ({ size, group, model, options }: DrawOpts) => {
  const numUsersGroup = getChild(group, groupIds.numUsers);

  if (!numUsersGroup) return;

  const isVisible = numUsersGroup.get('visible');

  // Don't bother with the rest of the calculations, as it is hidden anyway
  if (!shouldNumUsersGroupShow(model?.numUsers)) {
    if (isVisible) {
      numUsersGroup.hide();
    }
    return;
  } else if (!isVisible) {
    numUsersGroup.show();
  }

  const { iconX, iconY, text, textX, textY, wrapperHeight, wrapperRadius, wrapperWidth, wrapperX } =
    getNumUserDetails({ size, options, model });

  const wrapper = getChild(numUsersGroup, shapeNames.numUsersWrapper);

  wrapper.attr({
    x: wrapperX,
    width: wrapperWidth,
    height: wrapperHeight,
    radius: wrapperRadius,
  });

  const numUsersText = numUsersGroup.findByClassName(shapeNames.numUsersText);

  numUsersText.attr({
    text,
    x: textX,
    y: textY,
  });

  const icon = numUsersGroup.findByClassName(shapeNames.numUsersIcon);

  icon.attr({
    x: iconX,
    y: iconY,
  });
};

export const updateNumUsersPosition = ({ size, group, options }: Omit<DrawOpts, 'model'>) => {
  const { iconX, iconY, textX, textY, wrapperX } = getNumUserPositionDetails({ size, options });
  const wrapper = getChild(group, shapeNames.numUsersWrapper);

  // Position hasn't changed, so exit early to avoid unnessary changes
  if (wrapper?.attrs?.x === wrapperX) return;

  wrapper?.attr({ x: wrapperX });
  group.findByClassName(shapeNames.numUsersText)?.attr({ x: textX, y: textY });
  group.findByClassName(shapeNames.numUsersIcon)?.attr({ x: iconX, y: iconY });
};

export default {
  draw: drawNumUsers,
  update: updateNumUsers,
  updatePosition: updateNumUsersPosition,
  shouldBeVisible: shouldNumUsersGroupShow,
};
